import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Undo2,
  Redo2,
  Check,
  Plus,
  Bold,
  Italic,
  Underline,
  List,
  MoreHorizontal,
} from "lucide-react";
import aiIcon from "@/assets/9861-removebg-preview.png";
import { handleContentLinkClick } from "@/lib/link-utils";
import { markdownFromClipboard } from "@/lib/markdown";
import { ImageLightbox } from "@/components/image-lightbox";

import {
  useUpdateNote,
  useDeleteNote,
  useUploadImage,
  getListNotesQueryKey,
  getSearchNotesQueryKey,
  type Note,
} from "@workspace/api-client-react";

const AUTOSAVE_DELAY_MS = 500;
const HISTORY_GROUP_MS = 400;
const MAX_HISTORY = 500;

interface NoteOverlayProps {
  note: Note;
  onClose: () => void;
  /** Active search query, if the overlay was opened from search results. */
  searchQuery?: string;
}

type Snapshot = { title: string; content: string };

// Full-screen editor for a single note: title (plain) + content (rich text,
// stored as HTML), both auto-saved on a debounce. Closing (backdrop click or
// back button) always finalizes the last edit — the Save button is a shortcut.
export function NoteOverlay({ note, onClose, searchQuery }: NoteOverlayProps) {
  const queryClient = useQueryClient();
  const updateNote = useUpdateNote();
  const deleteNote = useDeleteNote();
  const uploadImage = useUploadImage();

  const [title, setTitle] = React.useState(note.title);
  const [history, setHistory] = React.useState<{
    stack: Snapshot[];
    index: number;
  }>(() => ({
    stack: [{ title: note.title, content: note.content }],
    index: 0,
  }));
  const [justSaved, setJustSaved] = React.useState(false);
  type LightboxImage =
    | { url: string; source: "strip" }
    | { url: string; source: "inline"; element: HTMLImageElement };
  const [lightboxImage, setLightboxImage] = React.useState<LightboxImage | null>(null);

  const lastSavedRef = React.useRef({
    title: note.title,
    content: note.content,
  });
  const draftRef = React.useRef({ title: note.title, content: note.content });
  const saveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const savedFlashTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isSearchActive = !!searchQuery;
  // contentEditable is uncontrolled: React never re-renders its innerHTML on
  // every keystroke (that would reset the cursor). We only imperatively set
  // innerHTML when switching notes or applying undo/redo.
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  // Last text-selection Range inside contentRef before an image thumbnail
  // was clicked, so "Paste down" can insert at that exact cursor position.
  const savedRangeRef = React.useRef<Range | null>(null);
  const addImageInputRef = React.useRef<HTMLInputElement | null>(null);
  const suppressImageGuardRef = React.useRef(false);
  // Strips only "color:" from inline styles (leaves border-color,
  // background-color, etc. untouched) so old notes with baked-in text
  // colors from other apps/themes always use the current theme color.
  // Marks every inline image as an atomic, non-editable unit. Called
  // whenever content is set on the editor (note switch, undo/redo) so
  // that images pasted down before this protection existed are covered
  // too, not just newly pasted ones.
  const protectInlineImages = (editor: HTMLDivElement | null) => {
    if (!editor) return;
    editor.querySelectorAll("img").forEach((img) => {
      img.setAttribute("contenteditable", "false");
    });
  };

  const stripInlineTextColor = (html: string): string => {
    return html.replace(/style="([^"]*)"/g, (_match, styleContent) => {
      const filtered = styleContent
        .split(";")
        .map((decl: string) => decl.trim())
        .filter((decl: string) => {
          if (!decl) return false;
          const prop = decl.split(":")[0]?.trim().toLowerCase();
          return prop !== "color";
        })
        .join("; ");
      return filtered ? `style="${filtered}"` : "";
    });
  };

  // Reset local state whenever a different note is opened
  React.useEffect(() => {
    setTitle(note.title);
    if (contentRef.current) {
      contentRef.current.innerHTML = stripInlineTextColor(note.content);
      protectInlineImages(contentRef.current);
    }
    lastSavedRef.current = { title: note.title, content: note.content };
    draftRef.current = { title: note.title, content: note.content };
    setHistory({
      stack: [{ title: note.title, content: note.content }],
      index: 0,
    });
  }, [note.id]);

  // Safety net: some mobile keyboards (notably Gboard on Android) can
  // delete an inline image via Backspace even when beforeinput's
  // preventDefault() is called — a known Android WebView/Chrome quirk.
  // Rather than fight that unreliably before the fact, watch for it after
  // the fact and restore the image immediately.
  React.useEffect(() => {
    const editor = contentRef.current;
    if (!editor) return;

    const observer = new MutationObserver((mutations) => {
      if (suppressImageGuardRef.current) return;
      let restored = false;
      for (const mutation of mutations) {
        mutation.removedNodes.forEach((node) => {
          const images: HTMLImageElement[] = [];
          if (node.nodeName === "IMG") {
            images.push(node as HTMLImageElement);
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            images.push(
              ...Array.from(
                (node as Element).querySelectorAll("img"),
              ),
            );
          }
          images.forEach((img) => {
            const target = mutation.target;
            const nextSibling = mutation.nextSibling;
            const prevSibling = mutation.previousSibling;
            if (nextSibling && editor.contains(nextSibling)) {
              target.insertBefore(img, nextSibling);
            } else if (prevSibling && editor.contains(prevSibling)) {
              target.insertBefore(img, prevSibling.nextSibling);
            } else {
              target.appendChild(img);
            }
            restored = true;
          });
        });
      }
      if (restored) {
        syncContentFromDom();
      }
    });

    observer.observe(editor, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [note.id]);

  const commitSave = React.useCallback(() => {
    const draft = draftRef.current;
    if (
      draft.title === lastSavedRef.current.title &&
      draft.content === lastSavedRef.current.content
    ) {
      return;
    }
    lastSavedRef.current = { ...draft };

    const plainContent = draft.content.replace(/<[^>]*>/g, "").trim();
    const hasInlineImage = /<img\b/i.test(draft.content);
    const hasStripImages = !!(note.imageUrls && note.imageUrls.length > 0);
    const isEmpty =
      !draft.title.trim() && !plainContent && !hasInlineImage && !hasStripImages;

    if (isEmpty) {
      deleteNote.mutate(
        { id: note.id },
        {
          onSuccess: () => {
            const remove = (list: Note[] | undefined) =>
              list?.filter((n) => n.id !== note.id);
            queryClient.setQueryData<Note[]>(getListNotesQueryKey(), remove);
            if (isSearchActive && searchQuery) {
              queryClient.setQueryData<Note[]>(
                getSearchNotesQueryKey({ q: searchQuery }),
                remove,
              );
            }
          },
        },
      );
      return;
    }

    updateNote.mutate(
      { id: note.id, data: { title: draft.title, content: draft.content } },
      {
        onSuccess: (updated) => {
          const patch = (list: Note[] | undefined) =>
            list?.map((n) => (n.id === updated.id ? updated : n));
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), patch);
          if (isSearchActive && searchQuery) {
            queryClient.setQueryData<Note[]>(
              getSearchNotesQueryKey({ q: searchQuery }),
              patch,
            );
          }
          setJustSaved(true);
          if (savedFlashTimerRef.current)
            clearTimeout(savedFlashTimerRef.current);
          savedFlashTimerRef.current = setTimeout(
            () => setJustSaved(false),
            1200,
          );
        },
      },
    );
  }, [
    note.id,
    updateNote,
    deleteNote,
    queryClient,
    isSearchActive,
    searchQuery,
  ]);

  const clearPendingSave = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  const scheduleSave = () => {
    clearPendingSave();
    saveTimerRef.current = setTimeout(commitSave, AUTOSAVE_DELAY_MS);
  };

  const pushHistory = (snap: Snapshot) => {
    setHistory((prev) => {
      const last = prev.stack[prev.index];
      if (last.title === snap.title && last.content === snap.content)
        return prev;
      const trimmed = prev.stack.slice(0, prev.index + 1);
      const nextStack = [...trimmed, snap].slice(-MAX_HISTORY);
      return { stack: nextStack, index: nextStack.length - 1 };
    });
  };

  const scheduleHistoryPush = (snap: Snapshot) => {
    if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    historyTimerRef.current = setTimeout(
      () => pushHistory(snap),
      HISTORY_GROUP_MS,
    );
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    draftRef.current = { ...draftRef.current, title: value };
    scheduleSave();
    scheduleHistoryPush({ ...draftRef.current });
  };

  // Called on every contentEditable input event (typing) and after every
  // toolbar formatting command.
  const syncContentFromDom = () => {
    const html = contentRef.current?.innerHTML ?? "";
    draftRef.current = { ...draftRef.current, content: html };
    scheduleSave();
    scheduleHistoryPush({ ...draftRef.current });
  };

  const applySnapshot = (snap: Snapshot) => {
    setTitle(snap.title);
    if (contentRef.current) {
      contentRef.current.innerHTML = stripInlineTextColor(snap.content);
      protectInlineImages(contentRef.current);
    }
    draftRef.current = { ...snap };
    scheduleSave();
  };

  const handleUndo = () => {
    setHistory((prev) => {
      if (prev.index === 0) return prev;
      const nextIndex = prev.index - 1;
      applySnapshot(prev.stack[nextIndex]);
      return { ...prev, index: nextIndex };
    });
  };

  const handleRedo = () => {
    setHistory((prev) => {
      if (prev.index >= prev.stack.length - 1) return prev;
      const nextIndex = prev.index + 1;
      applySnapshot(prev.stack[nextIndex]);
      return { ...prev, index: nextIndex };
    });
  };

  const canUndo = history.index > 0;
  const canRedo = history.index < history.stack.length - 1;

  const flushSave = () => {
    clearPendingSave();
    commitSave();
  };

  // Flush any pending save if the overlay unmounts without an explicit
  // close (e.g. browser back button navigating away).
  React.useEffect(() => {
    return () => {
      clearPendingSave();
      commitSave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id]);

  const handleBackdropClick = () => {
    flushSave();
    onClose();
  };

  const handleBack = () => {
    flushSave();
    onClose();
  };

  const handleSaveClick = () => {
    if (historyTimerRef.current) {
      clearTimeout(historyTimerRef.current);
      pushHistory({ ...draftRef.current });
    }
    flushSave();
  };

  // Toolbar formatting: mousedown preventDefault keeps the current text
  // selection alive (a plain click would blur the editor first and lose it).
  const applyFormat = (command: string) => {
    contentRef.current?.focus();
    document.execCommand(command, false);
    syncContentFromDom();
  };

  const toolbarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Clicking the blank space below the note's actual content (title, image
  // strips, text) should still focus the editor and place the cursor at the
  // end, since the scroll container is taller than its content.
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    const editor = contentRef.current;
    if (!editor) return;
    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  // Preserve the text cursor position when a thumbnail is clicked (a plain
  // click would blur the contentEditable and collapse the selection before
  // the lightbox even opens).
  const captureSelectionMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const sel = window.getSelection();
    if (
      sel &&
      sel.rangeCount > 0 &&
      contentRef.current?.contains(sel.anchorNode)
    ) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRangeRef.current = null;
    }
  };

  const updateImageList = (nextImageUrls: string[]) => {
    updateNote.mutate(
      { id: note.id, data: { imageUrls: nextImageUrls } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListNotesQueryKey() });
          if (isSearchActive && searchQuery) {
            queryClient.invalidateQueries({
              queryKey: getSearchNotesQueryKey({ q: searchQuery }),
            });
          }
        },
      },
    );
  };

  const handleRemoveImage = (img: LightboxImage) => {
    if (img.source === "strip") {
      updateImageList((note.imageUrls ?? []).filter((u) => u !== img.url));
    } else {
      suppressImageGuardRef.current = true;
      img.element.remove();
      syncContentFromDom();
      flushSave();
      suppressImageGuardRef.current = false;
    }
    setLightboxImage(null);
  };

  const handlePasteDown = (url: string) => {
    const editor = contentRef.current;
    if (!editor) return;

    editor.focus();
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      if (
        savedRangeRef.current &&
        editor.contains(savedRangeRef.current.startContainer)
      ) {
        sel.addRange(savedRangeRef.current);
      } else {
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        sel.addRange(range);
      }
    }

    const imgHtml = `<img src="${url}" alt="" contenteditable="false" style="max-width:100%;border-radius:1rem;display:block;margin:0.5rem 0;" />`;
    document.execCommand("insertHTML", false, imgHtml);
    syncContentFromDom();
    flushSave();

    updateImageList((note.imageUrls ?? []).filter((u) => u !== url));
    setLightboxImage(null);
    savedRangeRef.current = null;
  };

  const handleAddImage = () => {
    addImageInputRef.current?.click();
  };

  const handleAddImageFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    const results = await Promise.all(
      files.map((file) => uploadImage.mutateAsync({ data: { file } })),
    );
    updateImageList([...(note.imageUrls ?? []), ...results.map((r) => r.url)]);
  };

  // Prevent Backspace from deleting an inline image (or merging a line
  // into it in a way that removes it). Instead, the caret just moves to
  // just before the image — a further Backspace then acts on whatever
  // precedes the image, never the image itself.
  // True for a bare <img>, or for a wrapper (e.g. Chrome's per-line <div>)
  // whose only real content is exactly one <img> (ignoring whitespace text
  // nodes and stray <br>s).
  const isImageOnlyBlock = (node: Node): boolean => {
    if (node.nodeName === "IMG") return true;
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    let imgCount = 0;
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeName === "IMG") {
        imgCount += 1;
        continue;
      }
      if (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) {
        continue;
      }
      if (child.nodeName === "BR") continue;
      return false;
    }
    return imgCount === 1;
  };

  // If a backward-deletion at the current caret position would delete or
  // merge into an inline image, return that image/wrapper node so the
  // caller can preventDefault() and reposition the caret before it instead.
  const findBlockingImageForBackspace = (): Node | null => {
    const editor = contentRef.current;
    const sel = window.getSelection();
    if (!editor || !sel || sel.rangeCount === 0 || !sel.isCollapsed) {
      return null;
    }
    const range = sel.getRangeAt(0);
    let container: Node = range.startContainer;
    let offset = range.startOffset;

    if (container.nodeType === Node.TEXT_NODE) {
      if (offset > 0) return null;
      const parent = container.parentNode;
      if (!parent) return null;
      offset = Array.prototype.indexOf.call(parent.childNodes, container);
      container = parent;
    }

    while (true) {
      if (offset > 0) {
        const prev = container.childNodes[offset - 1];
        if (isImageOnlyBlock(prev)) return prev;
        const isEmptyText =
          prev.nodeType === Node.TEXT_NODE && !prev.textContent?.trim();
        if (isEmptyText || prev.nodeName === "BR") {
          offset -= 1;
          continue;
        }
        return null;
      }
      if (container === editor) return null;
      const parent: Node | null = container.parentNode;
      if (!parent) return null;
      offset = Array.prototype.indexOf.call(parent.childNodes, container);
      container = parent;
    }
  };

  const skipCaretBeforeNode = (node: Node) => {
    const sel = window.getSelection();
    const parent = node.parentNode;
    if (!sel || !parent) return;
    const idx = Array.prototype.indexOf.call(parent.childNodes, node);
    const newRange = document.createRange();
    newRange.setStart(parent, idx);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
  };

  // Desktop keyboards: a real "Backspace" keydown fires and is cancelable.
  const handleContentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Backspace") return;
    const blocker = findBlockingImageForBackspace();
    if (!blocker) return;
    e.preventDefault();
    skipCaretBeforeNode(blocker);
  };

  // Mobile/virtual keyboards (Gboard, etc.) often don't fire a proper
  // "Backspace" keydown at all — they go straight to a beforeinput event
  // with inputType "deleteContentBackward", which IS reliably cancelable.
  // This is the path that actually matters on Android/iOS.
  const handleContentBeforeInput = (e: React.FormEvent<HTMLDivElement>) => {
    const inputType = (e.nativeEvent as InputEvent).inputType;
    if (inputType !== "deleteContentBackward") return;
    const blocker = findBlockingImageForBackspace();
    if (!blocker) return;
    e.preventDefault();
    skipCaretBeforeNode(blocker);
  };

  // Combined click handler for the note body: handles link/bookmark clicks
  // (existing behavior) and opens the lightbox for any inline image.
  // If the user pastes plain text that looks like Markdown, convert it to
  // HTML before inserting so formatting (bold, lists, links) comes through
  // instead of landing as literal "**" / "- " characters. Untouched when the
  // clipboard already carries HTML (rich paste from another app) or when the
  // plain text doesn't look like Markdown.
  const handleContentPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = markdownFromClipboard(e.clipboardData);
    if (!html) return;

    e.preventDefault();
    document.execCommand("insertHTML", false, html);
    syncContentFromDom();
  };

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      e.preventDefault();
      setLightboxImage({
        url: (target as HTMLImageElement).src,
        source: "inline",
        element: target as HTMLImageElement,
      });
      return;
    }
    handleContentLinkClick(e);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-6 animate-in fade-in duration-150 h-[100dvh]"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) handleBackdropClick();
        }}
      >
        <div className="w-full h-[100dvh] sm:h-auto sm:max-h-[85vh] sm:max-w-2xl sm:rounded-2xl bg-card shadow-xl flex flex-col overflow-hidden animate-in sm:zoom-in-95 duration-150">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>

            <div className="flex items-center gap-1">
              <div className="flex items-center gap-4 mr-3">
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={!canUndo}
                  className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Undo"
                >
                  <Undo2 className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={handleRedo}
                  disabled={!canRedo}
                  className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Redo"
                >
                  <Redo2 className="h-5 w-5" />
                </button>
              </div>

              <button
                type="button"
                onClick={handleSaveClick}
                className="flex items-center gap-1.5 h-9 px-4 rounded-full gradient-primary text-white text-sm font-medium transition-opacity"
              >
                <Check className="h-4 w-4" />
                {justSaved ? "Saved" : "Save"}
              </button>
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto px-6 sm:px-8 pt-6 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onClick={handleContainerClick}
          >
            <input
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              onBlur={flushSave}
              placeholder="Title"
              className="w-full bg-transparent border-0 outline-none text-2xl font-semibold tracking-tight placeholder:text-muted-foreground/50 mb-3"
            />
            {note.imageUrls &&
              note.imageUrls.length > 0 &&
              note.imagePosition !== "after" && (
                <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden mb-4">
                  {note.imageUrls.map((url, i) => (
                    <img
                      key={url + i}
                      src={url}
                      alt=""
                      onMouseDown={captureSelectionMouseDown}
                      onClick={() => setLightboxImage({ url, source: "strip" })}
                      className={
                        (note.imageUrls!.length === 1
                          ? "w-full max-h-64 object-cover rounded-2xl shrink-0"
                          : "w-[80%] h-64 object-cover rounded-2xl shrink-0") +
                        " cursor-pointer"
                      }
                    />
                  ))}
                </div>
              )}
            <div
              ref={contentRef}
              contentEditable
              suppressContentEditableWarning
              onInput={syncContentFromDom}
              onBlur={flushSave}
              onPaste={handleContentPaste}
              onClick={handleContentClick}
              onKeyDown={handleContentKeyDown}
              onBeforeInput={handleContentBeforeInput}
              data-placeholder="Note something down..."
              className="w-full bg-transparent outline-none text-[15px] leading-relaxed min-h-[50px] empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/50 empty:before:pointer-events-none [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-0.5"
            />
            {note.imageUrls &&
              note.imageUrls.length > 0 &&
              note.imagePosition === "after" && (
                <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden mt-4">
                  {note.imageUrls.map((url, i) => (
                    <img
                      key={url + i}
                      src={url}
                      alt=""
                      onMouseDown={captureSelectionMouseDown}
                      onClick={() => setLightboxImage({ url, source: "strip" })}
                      className={
                        (note.imageUrls!.length === 1
                          ? "w-full max-h-64 object-cover rounded-2xl shrink-0"
                          : "w-[80%] h-64 object-cover rounded-2xl shrink-0") +
                        " cursor-pointer"
                      }
                    />
                  ))}
                </div>
              )}
          </div>

          {/* Bottom formatting toolbar. Insert (+), AI, and More are visual
            placeholders for now — wired up in a future update. */}
          <div className="shrink-0 px-4 pb-4 pt-2">
            <div className="flex items-center justify-between gap-1 rounded-full border bg-card/60 backdrop-blur-xl px-3 py-2 shadow-sm">
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Insert"
              >
                <Plus className="h-6 w-6" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyFormat("bold")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Bold"
              >
                <Bold className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyFormat("italic")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Italic"
              >
                <Italic className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyFormat("underline")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Underline"
              >
                <Underline className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyFormat("insertUnorderedList")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Bullet list"
              >
                <List className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="AI assistant"
              >
                <img
                  src={aiIcon}
                  alt=""
                  className="h-6 w-6 object-contain scale-225 translate-y-[0.5px]"
                />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                className="p-2 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity shrink-0"
                aria-label="More"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {lightboxImage && (
        <ImageLightbox
          url={lightboxImage.url}
          onClose={() => setLightboxImage(null)}
          onRemove={() => handleRemoveImage(lightboxImage)}
          onPasteDown={
            lightboxImage.source === "strip"
              ? () => handlePasteDown(lightboxImage.url)
              : undefined
          }
          onAddImage={handleAddImage}
        />
      )}
      <input
        ref={addImageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleAddImageFileChange}
      />
    </>
  );
}
